import { format, isValid } from 'date-fns';

const FIAT_CURRENCIES = new Set(['EUR', 'GBP', 'NGN', 'USD']);

// LATE_PAYMENT is money that arrived after the window closed — still a collection.
const COMPLETED_STATUSES = new Set(['SUCCESSFUL', 'UNDERPAID', 'OVERPAID', 'LATE_PAYMENT']);
const EXPIRED_STATUSES = new Set(['ABANDONED']);
const FAILED_STATUSES = new Set(['FAILED']);

const EM_DASH = '—';
const MINUS_SIGN = '−';

const normalize = (value) => String(value ?? '').toUpperCase();

const getTransactionStatus = (status) => {
  const normalizedStatus = normalize(status);

  if (COMPLETED_STATUSES.has(normalizedStatus)) return { label: 'Completed', tone: 'success' };
  if (FAILED_STATUSES.has(normalizedStatus)) return { label: 'Failed', tone: 'error' };
  if (EXPIRED_STATUSES.has(normalizedStatus)) return { label: 'Expired', tone: 'neutral' };
  return { label: 'Awaiting payment', tone: 'warning' };
};

// NOT_AVAILABLE means either side of the comparison is missing, which is the
// awaiting/expired/failed case the design shows with no badge.
const getPaymentClassification = (classification) => {
  switch (normalize(classification)) {
    case 'EXACT':
      return { label: 'Exact', tone: 'success' };
    case 'UNDERPAID':
      return { label: 'Underpaid', tone: 'warning' };
    case 'OVERPAID':
      return { label: 'Overpaid', tone: 'warning' };
    default:
      return null;
  }
};

// Returns null when there is nothing to show, so rows fall back to EmptyValue.
const formatAmount = (amount, currency) => {
  if (amount === null || amount === undefined || amount === '') return null;

  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) return null;

  const currencyCode = normalize(currency);

  if (FIAT_CURRENCIES.has(currencyCode)) {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numericAmount);
  }

  // Six decimals minimum so 0.000300 BTC lines up with 0.000345 BTC.
  const formattedAmount = new Intl.NumberFormat('en-US', {
    useGrouping: false,
    minimumFractionDigits: 6,
    maximumFractionDigits: 8
  }).format(numericAmount);
  return `${formattedAmount}${currencyCode ? ` ${currencyCode}` : ''}`;
};

const formatMoney = (money) => formatAmount(money?.amount, money?.currency);

const formatDifference = (money) => {
  const difference = money?.amount;
  if (difference === null || difference === undefined) return EM_DASH;

  const absoluteAmount = formatAmount(Math.abs(Number(difference)), money?.currency);
  if (absoluteAmount === null) return EM_DASH;
  if (difference > 0) return `+${absoluteAmount}`;
  if (difference < 0) return `${MINUS_SIGN}${absoluteAmount}`;
  return absoluteAmount;
};

const formatDetailDate = (date) => {
  if (!date) return null;
  const parsedDate = date instanceof Date ? date : new Date(date);
  return isValid(parsedDate) ? format(parsedDate, 'dd MMM yyyy, HH:mm') : null;
};

// TRANSACTION_ACTION values with design copy. Anything else is humanised rather
// than dropped, so no log entry disappears from the merchant's audit trail.
const TIMELINE_EVENTS = {
  CREATED: { label: 'Transaction created', tone: 'neutral' },
  INITIALIZED: { label: 'Payment initialized', tone: 'neutral' },
  DEPOSIT_QUEUED_FOR_PROCESSING: { label: 'Payment queued for processing', tone: 'warning' },
  DEPOSIT_TRANSACTION_CREATED: { label: 'Payment detected', tone: 'warning' },
  DEPOSIT_TRANSACTION_COMPLETED: { label: 'Payment received', tone: 'success' },
  WALLET_UPDATED: { label: 'Wallet credited', tone: 'success' },
  NOTIFICATION_SENT: { label: 'Webhook delivered', tone: 'success' },
  NOTIFICATION_FAILED: { label: 'Webhook delivery failed', tone: 'error' },
  NOTIFIED_VIA_DEPOSIT_WEBHOOK: { label: 'Merchant notified', tone: 'success' },
  NOTIFIED_VIA_ADDRESS_WEBHOOK: { label: 'Merchant notified', tone: 'success' },
  NOTIFIED_VIA_WITHDRAWAL_WEBHOOK: { label: 'Merchant notified', tone: 'success' },
  QUEUED_FOR_SETTLEMENT_CONVERSION: { label: 'Queued for settlement', tone: 'neutral' },
  SETTLEMENT_CONVERSION_SWAP_COMPLETE: { label: 'Settlement conversion complete', tone: 'success' },
  SETTLEMENT_CONVERSION_FAILED: { label: 'Settlement conversion failed', tone: 'error' },
  REFUND_CREATED: { label: 'Refund created', tone: 'neutral' },
  REFUND_COMPLETED: { label: 'Refund completed', tone: 'success' },
  REFUND_FAILED: { label: 'Refund failed', tone: 'error' },
  DISPUTE_CREATED: { label: 'Dispute created', tone: 'warning' },
  DISPUTE_RESOLVED: { label: 'Dispute resolved', tone: 'success' }
};

const humaniseEvent = (event) => {
  const words = String(event).toLowerCase().replace(/_/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Event';
};

const toneForUnknownEvent = (event) => {
  const normalizedEvent = normalize(event);
  if (normalizedEvent.includes('FAILED') || normalizedEvent.includes('REVERSED')) return 'error';
  if (normalizedEvent.includes('COMPLETED') || normalizedEvent.includes('CREDIT')) {
    return 'success';
  }
  return 'neutral';
};

// findTransactionLogs has no ORDER BY, so response order cannot be trusted.
const buildTransactionTimeline = (timeline = []) =>
  [...timeline]
    .sort((a, b) => new Date(a?.occurred_at ?? 0) - new Date(b?.occurred_at ?? 0))
    .map((entry) => {
      const known = TIMELINE_EVENTS[normalize(entry?.event)];

      return {
        label: known?.label ?? humaniseEvent(entry?.event),
        tone: known?.tone ?? toneForUnknownEvent(entry?.event),
        date: entry?.occurred_at ?? null
      };
    });

export {
  buildTransactionTimeline,
  EM_DASH,
  formatAmount,
  formatDetailDate,
  formatDifference,
  formatMoney,
  getPaymentClassification,
  getTransactionStatus
};
